import React, { Fragment, useState } from "react";
import { Card, Button, Upload, Modal, message, notification, Checkbox, Form, Input } from "antd";
import {
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { postData, deleteData } from "apiServices/common";
import { IOU_DOCUMENTS_REQUESTED_BY_USER } from "apiServices/API_ENDPOINTS";
import { checkPermission } from "./config/checkPermission";
import { ROLES } from "./constants/roles";
import { useParams } from "react-router";

// import { FILE_UPLOAD } from "../../Scripts/api";

const { confirm } = Modal;

// Function to format status for human-readable display
const formatStatus = (status) => {
  if (!status) return "-";

  // Convert to string and handle different cases
  const statusStr = String(status).toLowerCase();

  // Replace underscores with spaces and capitalize each word
  return statusStr
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export default function UploadDocument({
  DOC_ADD = `/api/exim-cost/iou_document/store`,
  filesList,
  setFileList,
  view,
  record,
  getIOUCostInfo,
}) {
  const [loading, setLoading] = useState(false);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [canApprove, setCanApprove] = useState(false);
  const [canReject, setCanReject] = useState(false);
  const [canRequest, setCanRequest] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [actionType, setActionType] = useState(null); // "approve" or "reject"
  const [commentForm] = Form.useForm();
  const { iouCostId } = useParams();

  const loggedInUserId = parseInt(localStorage.getItem("user_id"), 10);
  const loggedInUserRole = localStorage.getItem("user_type");

  // Check if user's role can approve or reject documents (to hide upload button)
  // Users with approve/reject permissions (Department Head, Director) should not see upload button
  const canUserApproveOrReject = React.useMemo(() => {
    if (!loggedInUserRole) return false;
    if(loggedInUserId !== record?.user?.id) return false;
    return true;

    // // Check if user is Department Head or Director (roles that can approve/reject)
    // const isDepartmentHead = loggedInUserRole === ROLES.DEPARTMENT_HEAD;
    // const isDirector = loggedInUserRole === ROLES.DIRECTOR;
    // const isAccountant = loggedInUserRole === ROLES.ACCOUNTS;

    // // If user is Department Head, check if they can approve/reject "requested" status documents
    // if (isDepartmentHead) {
    //   const canApproveRequested = checkPermission(
    //     loggedInUserRole,
    //     "request",
    //     false,
    //     "approve",
    //     record?.department_approve,
    //     record?.director_approve,
    //   );
    //   const canRejectRequested = checkPermission(
    //     loggedInUserRole,
    //     "request",
    //     false,
    //     "reject",
    //     record?.department_approve,
    //     record?.director_approve,
    //   );
    //   if (canApproveRequested || canRejectRequested) return true;
    // }

    // // If user is Director, check if they can approve/reject "by_department" status documents
    // if (isDirector) {
    //   const canApproveByDept = checkPermission(
    //     loggedInUserRole,
    //     "by_department",
    //     false,
    //     "approve",
    //     record?.department_approve,
    //     record?.director_approve,
    //   );
    //   const canRejectByDept = checkPermission(
    //     loggedInUserRole,
    //     "by_department",
    //     false,
    //     "reject",
    //     record?.department_approve,
    //     record?.director_approve,
    //   );
    //   if (canApproveByDept || canRejectByDept) return true;
    // }

    // if (isAccountant) {
    //   const canApproveByAccount = checkPermission(
    //     loggedInUserRole,
    //     "by_director",
    //     false,
    //     "approve",
    //     record?.department_approve,
    //     record?.director_approve,
    //     record?.account_approve,
    //   );
    //   const canRejectByAccount = checkPermission(
    //     loggedInUserRole,
    //     "by_director",
    //     false,
    //     "reject",
    //     record?.department_approve,
    //     record?.director_approve,
    //     record?.account_approve,
    //   );
    //   if (canApproveByAccount || canRejectByAccount) return true;
    // }
  }, [loggedInUserRole, record]);

  // const base_url = process.env.REACT_APP_BASE;
  const api_base_url = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");
  const props = {
    accept: "image/*",
    showUploadList: false,
    customRequest({ file, onSuccess, onError }) {
      setLoading(true);
      const formData = new FormData();
      formData.append("document", file);
      formData.append("iou_id", record.id);
      formData.append("document_name", file.name);

      fetch(api_base_url + DOC_ADD, {
        method: "POST",
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      })
        .then((res) => res.json())
        .then((response) => {
          onSuccess(response, file);
          setLoading(false);
        })
        .catch((err) => {
          onError(err);
          setLoading(false);
        });
    },
    async onChange(info) {
      if (info.file.status !== "uploading") {
      }
      if (info.file.status === "done" && info?.file?.response.code === 200) {
        let content = {
          id: info?.file?.response?.data?.id,
          name: info?.file?.response?.data?.document_name,
          path: info?.file?.response?.data?.document,
          status: info?.file?.response?.data?.status || "created",
        };

        setFileList((oldArray) => [...oldArray, content]);

        message.success(`Document uploaded successfully`);
      } else if (info.file.status === "done" && info?.file?.response.code !== 200) {
        message.error(`Document file upload failed.`);
      } else if (info.file.status === "error") {
        message.error(`Document file upload failed.`);
      }
    },
  };

  const removeFile = async (index, fileId) => {
    const file = filesList.find((doc) => doc.id === fileId);

    // Check if user is creator
    const isCreator = loggedInUserId === record?.user?.id;

    // Check if status is "created"
    const canDelete = file?.status === "created";

    if (!isCreator) {
      notification.warning({
        message: "Warning",
        description: "Only the creator can delete documents.",
        placement: "bottomRight",
      });
      return;
    }

    if (!canDelete) {
      notification.warning({
        message: "Warning",
        description: "Only documents with 'created' status can be deleted.",
        placement: "bottomRight",
      });
      return;
    }

    confirm({
      title: "Do you Want to delete this document?",
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        try {
          setLoading(true);
          const response = await deleteData(`/api/exim-cost/iou_document/delete/${fileId}`);

          if (response && (response?.code === 200 || response?.status === 200)) {
            const items = filesList.filter((doc) => doc.id !== fileId);
            setFileList(items);

            message.success("Document deleted successfully");

            // Refresh IOU cost info if callback is provided
            if (getIOUCostInfo && iouCostId) {
              getIOUCostInfo(iouCostId);
            }
          } else {
            notification.error({
              message: "Error",
              description: response?.message || "Failed to delete document",
              placement: "bottomRight",
            });
          }
        } catch (error) {
          notification.error({
            message: "Error",
            description: "Failed to delete document",
            placement: "bottomRight",
          });
        } finally {
          setLoading(false);
        }
      },
      onCancel() {},
    });
  };

  const handleCheckboxChange = (checked, fileId) => {
    if (!fileId) return;

    const newSelectedIds = checked
      ? [...selectedDocumentIds, fileId]
      : selectedDocumentIds.filter((id) => id !== fileId);

    if (newSelectedIds.length === 0) {
      setSelectedDocumentIds([]);
      setCanApprove(false);
      setCanReject(false);
      setCanRequest(false);
      return;
    }

    const selectedDocs = newSelectedIds.map((id) => filesList.find((doc) => doc.id === id)).filter((doc) => !!doc);

    const allSameStatus = selectedDocs.length > 0 && selectedDocs.every((doc) => doc.status === selectedDocs[0].status);

    if (!allSameStatus) {
      notification.warning({
        message: "Warning",
        description: "Only documents with the same status can be selected.",
        placement: "bottomRight",
      });
      setCanApprove(false);
      setCanReject(false);
      setCanRequest(false);
      return;
    }

    // Check permissions for approve, reject, and request
    const canRequestStatus = selectedDocs.some((doc) =>
      checkPermission(
        loggedInUserRole,
        doc.status,
        loggedInUserId === record?.user?.id,
        "request",
        record?.department_approve,
        record?.director_approve,
      ),
    );

    const canApproveStatus = selectedDocs.some((doc) =>
      checkPermission(
        loggedInUserRole,
        doc.status,
        loggedInUserId === record?.user?.id,
        "approve",
        record?.department_approve,
        record?.director_approve,
        record?.account_approve,
      ),
    );

    const canRejectStatus = selectedDocs.some((doc) =>
      checkPermission(
        loggedInUserRole,
        doc.status,
        loggedInUserId === record?.user?.id,
        "reject",
        record?.department_approve,
        record?.director_approve,
        record?.account_approve,
      ),
    );

    setCanRequest(canRequestStatus);
    setCanApprove(canApproveStatus);
    setCanReject(canRejectStatus);
    setSelectedDocumentIds(newSelectedIds);
  };

  const handleHeaderCheckboxChange = (e) => {
    const { checked } = e.target;

    if (!checked) {
      setSelectedDocumentIds([]);
      setCanApprove(false);
      setCanReject(false);
      setCanRequest(false);
      return;
    }

    const eligibleDocs = filesList.filter((doc) =>
      ["created", "rejected", "requested", "by_department", "by_director", "by_account"].includes(doc.status),
    );

    if (!eligibleDocs.length) {
      notification.warning({
        message: "Warning",
        description: "No eligible documents to select.",
        placement: "bottomRight",
      });
      return;
    }

    const allSameStatus = eligibleDocs.length > 0 && eligibleDocs.every((doc) => doc.status === eligibleDocs[0].status);

    if (!allSameStatus) {
      notification.warning({
        message: "Warning",
        description: "Only documents with the same status can be selected.",
        placement: "bottomRight",
      });
      return;
    }

    // Check permissions
    const canRequestStatus = eligibleDocs.some((doc) =>
      checkPermission(
        loggedInUserRole,
        doc.status,
        loggedInUserId === record?.user?.id,
        "request",
        record?.department_approve,
        record?.director_approve,
      ),
    );

    const canApproveStatus = eligibleDocs.some((doc) =>
      checkPermission(
        loggedInUserRole,
        doc.status,
        loggedInUserId === record?.user?.id,
        "approve",
        record?.department_approve,
        record?.director_approve,
        record?.account_approve,
      ),
    );

    const canRejectStatus = eligibleDocs.some((doc) =>
      checkPermission(
        loggedInUserRole,
        doc.status,
        loggedInUserId === record?.user?.id,
        "reject",
        record?.department_approve,
        record?.director_approve,
        record?.account_approve,
      ),
    );

    setCanRequest(canRequestStatus);
    setCanApprove(canApproveStatus);
    setCanReject(canRejectStatus);
    setSelectedDocumentIds(eligibleDocs.map((doc) => doc.id));
  };

  const handleRequestClick = async () => {
    if (!selectedDocumentIds.length) {
      return;
    }

    const selectedDocs = selectedDocumentIds.map((id) => filesList.find((doc) => doc.id === id)).filter((doc) => !!doc);

    if (!selectedDocs.length) return;

    const allSameStatus = selectedDocs.every((doc) => doc.status === selectedDocs[0].status);
    const currentStatus = selectedDocs[0]?.status;
    const isAllowedStatus =
      currentStatus === "created" || currentStatus === "rejected" || currentStatus === "by_department";

    if (!allSameStatus || !isAllowedStatus) {
      notification.warning({
        message: "Warning",
        description:
          "Only documents with the same status can be requested, and only 'created' or 'rejected' documents can be requested.",
        placement: "bottomRight",
      });
      return;
    }

    try {
      const ids = [];
      selectedDocumentIds.forEach((id) => {
        ids.push(id);
      });

      const payload = {
        requested_id: ids,
        status: "requested",
      };

      const response = await postData(IOU_DOCUMENTS_REQUESTED_BY_USER, payload);

      if (response && (response?.code === 200 || response?.status === 200)) {
        notification.success({
          message: "Success",
          description: response?.message || "Documents requested successfully",
          placement: "bottomRight",
        });

        const updatedFiles = filesList.map((file) =>
          selectedDocumentIds.includes(file.id)
            ? {
                ...file,
                status: "requested",
              }
            : file,
        );

        setFileList(updatedFiles);
        setSelectedDocumentIds([]);
        setCanRequest(false);
        setCanApprove(false);
        setCanReject(false);
        getIOUCostInfo(iouCostId);
      } else {
        notification.error({
          message: "Error",
          description: "Failed to request documents",
          placement: "bottomRight",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to request documents",
        placement: "bottomRight",
      });
    }
  };

  const handleApproveClick = () => {
    if (!selectedDocumentIds.length) {
      return;
    }

    setActionType("approve");
    setModalVisible(true);
    commentForm.resetFields();
  };

  const handleRejectClick = () => {
    if (!selectedDocumentIds.length) {
      return;
    }

    setActionType("reject");
    setModalVisible(true);
    commentForm.resetFields();
  };

  const handleModalOk = async () => {
    try {
      const values = await commentForm.validateFields();
      const comment = values.comments || "";

      // For reject, comment is required
      if (actionType === "reject" && !comment.trim()) {
        notification.warning({
          message: "Warning",
          description: "Comment is required for rejection.",
          placement: "bottomRight",
        });
        return;
      }

      const selectedDocs = selectedDocumentIds
        .map((id) => filesList.find((doc) => doc.id === id))
        .filter((doc) => !!doc);

      if (!selectedDocs.length) return;

      const allSameStatus = selectedDocs.every((doc) => doc.status === selectedDocs[0].status);

      if (!allSameStatus) {
        notification.warning({
          message: "Warning",
          description: "Only documents with the same status can be processed.",
          placement: "bottomRight",
        });
        return;
      }

      const ids = [];
      selectedDocumentIds.forEach((id) => {
        ids.push(id);
      });

      const status = actionType === "approve" ? "approved" : "rejected";

      const payload = {
        requested_id: ids,
        status: status,
        comments: comment.trim(),
      };

      const response = await postData(IOU_DOCUMENTS_REQUESTED_BY_USER, payload);

      if (response && (response?.code === 200 || response?.status === 200)) {
        notification.success({
          message: "Success",
          description: response?.message || `Documents ${actionType}d successfully`,
          placement: "bottomRight",
        });

        const updatedFiles = filesList.map((file) =>
          selectedDocumentIds.includes(file.id)
            ? {
                ...file,
                status: status,
                comments: comment.trim() || file.comments,
              }
            : file,
        );

        setFileList(updatedFiles);
        setSelectedDocumentIds([]);
        setCanRequest(false);
        setCanApprove(false);
        setCanReject(false);
        setModalVisible(false);
        commentForm.resetFields();
        getIOUCostInfo(iouCostId);
      } else {
        notification.error({
          message: "Error",
          description: `Failed to ${actionType} documents`,
          placement: "bottomRight",
        });
      }
    } catch (error) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      notification.error({
        message: "Error",
        description: `Failed to ${actionType} documents`,
        placement: "bottomRight",
      });
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setActionType(null);
    commentForm.resetFields();
  };

  return (
    <Card className="mt-0 custome-table" title="IOU Document">
      <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div />
        <div style={{ display: "flex", gap: 10, minWidth: "300px", justifyContent: "flex-end" }}>
          {selectedDocumentIds.length > 0 && canReject && (
            <Button
              type="danger"
              icon={<CloseOutlined style={{ color: "white" }} />}
              onClick={handleRejectClick}
              style={{
                backgroundColor: "#ff4d4f",
                borderColor: "#ff4d4f",
                color: "white",
                padding: "0 8px",
              }}
            >
              Reject
            </Button>
          )}
          {selectedDocumentIds.length > 0 && canRequest && (
            <Button type="primary" size="small" onClick={handleRequestClick}>
              Request
            </Button>
          )}
          {selectedDocumentIds.length > 0 && canApprove && (
            <Button
              type="success"
              icon={<CheckOutlined style={{ color: "white" }} />}
              onClick={handleApproveClick}
              style={{
                backgroundColor: "#52c41a",
                borderColor: "#52c41a",
                color: "white",
                padding: "0 8px",
              }}
            >
              Approve
            </Button>
          )}
        </div>
      </div>
      <div className="root-table">
        <table>
          <thead>
            <tr>
              <th className="t-header bc-java">
                <Checkbox
                  onChange={handleHeaderCheckboxChange}
                  indeterminate={
                    selectedDocumentIds.length > 0 &&
                    selectedDocumentIds.length <
                      filesList.filter((doc) =>
                        ["created", "rejected", "requested", "by_department", "by_director", "by_account"].includes(
                          doc.status,
                        ),
                      ).length
                  }
                  checked={
                    filesList.filter((doc) =>
                      ["created", "rejected", "requested", "by_department", "by_director", "by_account"].includes(
                        doc.status,
                      ),
                    ).length > 0 &&
                    selectedDocumentIds.length > 0 &&
                    selectedDocumentIds.length ===
                      filesList.filter((doc) =>
                        ["created", "rejected", "requested", "by_department", "by_director", "by_account"].includes(
                          doc.status,
                        ),
                      ).length
                  }
                />
              </th>
              <th className="t-header bc-java">Serial</th>
              <th className="t-header bc-java">Document Name</th>
              <th className="t-header bc-java">File</th>
              <th className="t-header bc-java">Status</th>
              <th className="t-header bc-java">Comments</th>
              <th className="t-header bc-java"></th>
            </tr>
          </thead>
          <tbody>
            {filesList?.length ? (
              <>
                {filesList.map((file, index) => (
                  <Fragment key={index}>
                    <tr>
                      <td className="t-body">
                        {!view && (
                          <Checkbox
                            checked={selectedDocumentIds.includes(file.id)}
                            disabled={
                              ![
                                "created",
                                "rejected",
                                "requested",
                                "by_department",
                                "by_director",
                                "by_account",
                              ].includes(file.status)
                            }
                            onChange={(e) => handleCheckboxChange(e.target.checked, file.id)}
                          />
                        )}
                      </td>
                      <td className="t-body">{index + 1}</td>
                      <td className="t-body">{file.name}</td>
                      <td className="t-body">
                        <Button type="text" onClick={() => window.open(api_base_url + "/" + file.path, "_blank")}>
                          <EyeOutlined />
                        </Button>
                      </td>
                      <td className="t-body">
                        {file.status ? (
                          <span
                            style={{
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: 4,
                              color: "#fff",
                              fontSize: 12,
                              textTransform: "capitalize",
                              backgroundColor:
                                file.status === "requested" || file.status === "requested_by_user"
                                  ? "#fa8c16" // orange
                                  : file.status === "created"
                                  ? "#1890ff" // blue
                                  : file.status === "rejected"
                                  ? "#ff4d4f" // red
                                  : file.status === "approved" || file.status === "approved_by_director"
                                  ? "#52c41a" // green
                                  : file.status === "by_department" || file.status === "approved_by_dept_head"
                                  ? "#13c2c2" // cyan
                                  : file.status === "by_director"
                                  ? "#722ed1" // purple
                                  : file.status === "by_account"
                                  ? "#eb2f96" // pink
                                  : "#595959", // default gray
                            }}
                          >
                            {formatStatus(file.status)}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="t-body">{file.comments || "-"}</td>
                      {!view && (
                        <td className="t-body">
                          {loggedInUserId === record?.user?.id && file.status === "created" ? (
                            <DeleteOutlined
                              style={{ fontSize: "20px", color: "red" }}
                              className="cursore-pointer"
                              onClick={() => removeFile(index, file.id)}
                            />
                          ) : null}
                        </td>
                      )}
                    </tr>
                  </Fragment>
                ))}
              </>
            ) : (
              ""
            )}

            <tr>
              <td className="t-body"></td>
              <td className="t-body"></td>
              <td className="t-body"></td>
              <td className="t-body">
                {!view && canUserApproveOrReject && (
                  <Upload {...props}>
                    <Button type="primary" icon={<DownloadOutlined />} loading={loading}>
                      Upload
                    </Button>
                  </Upload>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Modal
        title={actionType === "approve" ? "Approve Documents" : "Reject Documents"}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={actionType === "approve" ? "Approve" : "Reject"}
        okButtonProps={{
          style:
            actionType === "reject"
              ? {
                  backgroundColor: "#ff4d4f",
                  borderColor: "#ff4d4f",
                  color: "white",
                }
              : {
                  backgroundColor: "#52c41a",
                  borderColor: "#52c41a",
                  color: "white",
                },
        }}
      >
        <Form form={commentForm} layout="vertical">
          <Form.Item
            name="comments"
            label="Comments"
            rules={
              actionType === "reject"
                ? [
                    {
                      required: true,
                      message: "Comment is required for rejection.",
                    },
                  ]
                : []
            }
          >
            <Input.TextArea
              rows={5}
              placeholder={
                actionType === "reject" ? "Please provide a reason for rejection" : "Add comments (optional)"
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
